// Fase 27A: helpers PUROS de ruteo/presentacion del signup+onboarding (separados para probar).
// ONB-001 (Macrofase A) añade dos pantallas públicas: continuar (correo + contraseña, para reanudar) y
// preparar el negocio (bienvenida + negocio y primera tienda).

export type SelfServiceView =
  | { view: 'signup' }
  | { view: 'verify'; token: string | null }
  | { view: 'continue' }
  | { view: 'setup' }
  | { view: 'onboarding' }
  // ONB-010: la invitación a la beta privada (el enlace del correo) y su «Prepara tu negocio».
  | { view: 'invite'; token: string | null }
  | { view: 'invite-setup' };

// Parsea el hash (#signup, #signup/verify?token=..., #signup/continue, #signup/setup, #onboarding). El
// token puede venir como query (?token=) o como segmento (#signup/verify/<token>).
// ONB-010: #beta-invite?token=... (o #beta-invite/<token>) y #beta-invite/setup.
export function parseSelfServiceHash(hash: string): SelfServiceView {
  const raw = hash.replace(/^#/, '');
  const [pathPart, queryPart] = raw.split('?');
  const seg = pathPart.split('/').filter(Boolean); // ['signup','verify'] | ['onboarding']
  if (seg[0] === 'onboarding') return { view: 'onboarding' };
  if (seg[0] === 'beta-invite') {
    if (seg[1] === 'setup') return { view: 'invite-setup' };
    return { view: 'invite', token: new URLSearchParams(queryPart ?? '').get('token') ?? seg[1] ?? null };
  }
  if (seg[0] === 'signup' && seg[1] === 'verify') {
    const fromQuery = new URLSearchParams(queryPart ?? '').get('token');
    const token = fromQuery ?? seg[2] ?? null;
    return { view: 'verify', token };
  }
  if (seg[0] === 'signup' && seg[1] === 'continue') return { view: 'continue' };
  if (seg[0] === 'signup' && seg[1] === 'setup') return { view: 'setup' };
  return { view: 'signup' };
}

export function isSelfServiceHash(hash: string): boolean {
  const seg = hash.replace(/^#/, '').split('?')[0].split('/').filter(Boolean);
  return seg[0] === 'signup' || seg[0] === 'onboarding' || seg[0] === 'beta-invite';
}

// ONB-010: la invitación a la beta privada. Mensajes humanos por estado; nunca códigos técnicos crudos.
export function inviteErrorMessage(status: number, code?: string, fallback = 'No pudimos abrir tu invitación.'): string {
  if (code === 'PRIVATE_BETA_INVITE_EXPIRED') return 'Tu invitación venció. Pide a quien te invitó que te la vuelva a enviar.';
  if (code === 'PRIVATE_BETA_INVITE_REVOKED') return 'Esta invitación ya no está disponible.';
  // P-085: la beta está cerrada a testers nuevos (las cuentas ya creadas siguen).
  if (code === 'PRIVATE_BETA_INVITE_CLOSED') return 'Por ahora la beta privada no está recibiendo cuentas nuevas. Pide a quien te invitó que te avise cuando vuelva a abrir.';
  if (code === 'PRIVATE_BETA_INVITE_INVALID') return 'Este enlace de invitación no es válido. Si te enviaron uno más nuevo, usa ese.';
  if (code === 'PRIVATE_BETA_INVALID_CREDENTIALS') return 'La contraseña no coincide con la que elegiste al crear tu cuenta.';
  if (status === 404) return 'Las invitaciones no están disponibles en este momento.';
  if (status === 429) return 'Demasiados intentos. Espera unos minutos e inténtalo de nuevo.';
  if (status === 400) return 'Revisa los datos: tu nombre y una contraseña de al menos 8 caracteres con letras y números.';
  if (status === 0) return 'No fue posible conectar con el servidor. Revisa tu conexión.';
  return fallback;
}

const NEXT_ACTION_LABEL: Record<string, string> = {
  CONFIGURE_FIRST_STORE: 'Configura tu primera tienda',
  COMPLETE_ONBOARDING: 'Finaliza la configuración',
};
export function nextActionLabel(action: string | null): string {
  if (!action) return 'Todo listo';
  return NEXT_ACTION_LABEL[action] ?? action;
}

// Fase 27B: zonas horarias frecuentes en México con etiqueta comprensible (no offset fijo). El backend
// valida IANA; estas son solo sugerencias. La zona detectada del navegador se antepone en la UI.
export const TIMEZONE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'America/Mexico_City', label: 'Centro (Ciudad de México, Guadalajara)' },
  { value: 'America/Monterrey', label: 'Centro (Monterrey)' },
  { value: 'America/Merida', label: 'Sureste (Mérida)' },
  { value: 'America/Cancun', label: 'Quintana Roo (Cancún)' },
  { value: 'America/Chihuahua', label: 'Pacífico (Chihuahua)' },
  { value: 'America/Mazatlan', label: 'Pacífico (Mazatlán, La Paz)' },
  { value: 'America/Tijuana', label: 'Noroeste (Tijuana)' },
  // ONB-001: Sonora no cambia de horario y no coincide con ninguna de las de arriba.
  { value: 'America/Hermosillo', label: 'Sonora (Hermosillo)' },
];

// Etiqueta amigable de una zona IANA; si no está en el catálogo, devuelve la propia zona (nunca vacío).
export function friendlyTimezone(tz: string): string {
  return TIMEZONE_OPTIONS.find((o) => o.value === tz)?.label ?? tz;
}

// ONB-001: todas las zonas IANA de México (el catálogo de arriba más las que comparten horario con alguna).
const MEXICO_TIMEZONES = new Set([
  ...TIMEZONE_OPTIONS.map((o) => o.value),
  'America/Bahia_Banderas', 'America/Matamoros', 'America/Ciudad_Juarez', 'America/Ojinaga',
]);
export const isMexicanTimezone = (tz: string): boolean => MEXICO_TIMEZONES.has(tz);

// ONB-001: una zona fuera del catálogo, dicha como la lee una persona: «Chicago (hora de verano central)».
export function humanTimezone(tz: string): string {
  const known = TIMEZONE_OPTIONS.find((o) => o.value === tz)?.label;
  if (known) return known;
  const city = (tz.split('/').pop() ?? tz).replace(/_/g, ' ');
  try {
    const name = new Intl.DateTimeFormat('es-MX', { timeZone: tz, timeZoneName: 'long' }).formatToParts(new Date()).find((part) => part.type === 'timeZoneName')?.value;
    return name ? `${city} (${name})` : city;
  } catch {
    return tz;
  }
}

// ONB-001: la zona del navegador si es válida; si no, la del Centro de México.
export function detectTimezone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) { new Intl.DateTimeFormat('en-US', { timeZone: tz }).format(); return tz; }
  } catch { /* zona desconocida para este navegador */ }
  return 'America/Mexico_City';
}

// ONB-001: la que se propone. El negocio está en México: si el equipo está en otra zona (de viaje, mal
// configurado), se propone la del Centro y la del equipo queda como opción.
export function suggestTimezone(detected: string): string {
  return isMexicanTimezone(detected) ? detected : 'America/Mexico_City';
}

// ONB-001: las opciones del selector. La del equipo se marca «detectada»; si no es de México, va al final.
export function timezoneChoices(detected: string): Array<{ value: string; label: string }> {
  const out = TIMEZONE_OPTIONS.map((opt) => (opt.value === detected ? { ...opt, label: `${opt.label} · detectada` } : { ...opt }));
  if (!TIMEZONE_OPTIONS.some((opt) => opt.value === detected)) {
    const extra = { value: detected, label: `${humanTimezone(detected)} · detectada` };
    if (isMexicanTimezone(detected)) out.unshift(extra); else out.push(extra);
  }
  return out;
}

// ONB-001: la ayuda bajo el selector, en palabras.
export function timezoneHint(detected: string): string {
  return isMexicanTimezone(detected)
    ? 'Es la de este equipo. Con esta hora se fechan tus ventas y tus cortes.'
    : `Este equipo está en otra zona: ${humanTimezone(detected).replace(' (', ', ').replace(/\)$/, '')}. Te sugerimos la del Centro de México. Con esta hora se fechan tus ventas y tus cortes.`;
}

// El primer nombre para saludar («Hola, Ana»). Nunca vacío: sin nombre, sin saludo personal.
export function firstNameOf(displayName: string | null | undefined): string | null {
  const first = (displayName ?? '').trim().split(/\s+/)[0];
  return first ? first : null;
}

// Mapea errores de dominio del signup a mensajes humanos en español. Mantiene la neutralidad (no revela
// existencia de cuentas). Nunca expone Prisma/SQL/stack ni códigos técnicos crudos.
export function signupErrorMessage(status: number, code?: string, fallback = 'No fue posible completar el registro.'): string {
  if (status === 404) return 'El registro no está disponible en este momento.';
  if (status === 429) return 'Demasiadas solicitudes. Espera un momento e inténtalo de nuevo.';
  if (status === 502 || code === 'SIGNUP_EMAIL_DELIVERY_FAILED') return 'No pudimos enviar el correo en este momento. Inténtalo de nuevo en unos minutos.';
  if (status === 400) return 'Revisa los datos: tu nombre, tu correo y una contraseña de al menos 8 caracteres con letras y números.';
  if (status === 0) return 'No fue posible conectar con el servidor. Revisa tu conexión.';
  return fallback;
}

// Mensaje para los estados de la verificación de correo (token expirado/usado/inválido).
export function verifyErrorMessage(status: number, backendMessage?: string): string {
  if (status === 0) return 'No fue posible conectar con el servidor. Vuelve a abrir el enlace.';
  if (status === 429) return 'Demasiados intentos. Espera un momento y vuelve a abrir el enlace.';
  // El backend ya devuelve un mensaje neutral y humano para token inválido/expirado; se reusa si viene.
  return backendMessage || 'El enlace no es válido o expiró. Solicita uno nuevo desde “Crear cuenta”.';
}

// ONB-001: continuar con correo + la contraseña de la cuenta. Neutral: no dice si el correo existe.
export function continueErrorMessage(status: number): string {
  if (status === 401) return 'El correo o la contraseña no coinciden con un registro pendiente.';
  if (status === 400) return 'Revisa tu correo y tu contraseña.';
  if (status === 404) return 'El registro no está disponible en este momento.';
  if (status === 429) return 'Demasiados intentos. Espera unos minutos e inténtalo de nuevo.';
  if (status === 0) return 'No fue posible conectar con el servidor. Revisa tu conexión.';
  return 'No pudimos continuar. Inténtalo de nuevo.';
}

// ONB-001: preparar el negocio. ONB-002: un 401 es que la sesión corta de registro ya no está (venció o se
// abrió en otro navegador): se entra con correo y contraseña.
export function setupErrorMessage(status: number, code?: string): string {
  if (code === 'SIGNUP_EMAIL_NOT_VERIFIED') return 'Primero confirma tu correo con el enlace que te enviamos.';
  if (code === 'ONBOARDING_INVALID_TIMEZONE') return 'Elige una zona horaria de la lista.';
  if (code === 'SIGNUP_UNSUPPORTED_COUNTRY_CURRENCY') return 'Elige un país y una moneda de la lista.';
  if (code === 'SIGNUP_ALREADY_PROVISIONED') return 'Tu negocio ya está creado. Entra con tu correo y tu contraseña.';
  if (status === 401) return 'Para seguir, entra con tu correo y tu contraseña.';
  if (status === 400) return 'Revisa los datos: el nombre del negocio necesita al menos 2 letras y el de la sucursal al menos 1.';
  if (status === 404) return 'El registro no está disponible en este momento.';
  if (status === 429) return 'Demasiados intentos. Espera unos minutos e inténtalo de nuevo.';
  if (status === 0) return 'No fue posible conectar con el servidor. Tus datos siguen aquí: vuelve a intentarlo.';
  return 'No pudimos preparar tu negocio. Inténtalo de nuevo.';
}
